import { usePersistedState } from "@/lib/persistedFilters";
// Iter 91l — Network Assessment Keys inventory.
//
// Staff-only inventory of UUID licence keys handed out to partners for their
// on-site network assessments.  Seeded from a UC internal CSV export on
// first boot; recruiters can add/edit/approve/import going forward.
//
// UX borrows the Hardware/DidMaster pattern:
//   • Search + Available/Broken filters
//   • Sortable columns via ColumnsToolbar (show/hide, reorder, reset)
//   • CSV/PDF export
//   • CSV import (upsert on serial_key)
//   • Approver dropdown — backend enforces "you can only approve as yourself"
//     (audit rule); the UI mirrors that by disabling every option except the
//     current user's own row.

import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import {
  KeyRound, Plus, Search, Pencil, Trash2, Loader2, Upload, Save, X,
  CheckCircle2, Wrench, ShieldCheck, History, ArrowUpDown, ArrowUp, ArrowDown,
  Users, Ticket, CheckSquare, Square, Flame,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { ColumnsToolbar } from "@/components/ColumnsToolbar";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { useTablePagination, TablePagination, PageSizeSelect } from "@/components/TablePagination";

const fmtDT = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(); } catch { return String(iso); }
};

// Column catalogue — order defines default display order; `always` pins the
// column and hides the reorder/hide chrome for it in ColumnsToolbar.
const COLUMNS = [
  { key: "key_id",        label: "Key #",     always: true,
    text: (r) => r.key_id || "",
    render: (r) => <span className="font-mono text-[11px] text-slate-700">{r.key_id}</span> },
  { key: "is_available",  label: "Available",
    text: (r) => (r.is_available ? "YES" : "NO"),
    render: (r) => r.is_available
      ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700"><CheckCircle2 className="h-3 w-3" />Available</span>
      : <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500">In use</span> },
  { key: "broken",        label: "Broken",
    text: (r) => (r.broken ? (r.broken_reason ? `YES · ${r.broken_reason}` : "YES") : "NO"),
    render: (r) => r.broken
      ? <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-medium text-rose-700"
              title={r.broken_reason || "No reason on file"}>
          <Wrench className="h-3 w-3" />
          Broken{r.broken_reason ? " ⓘ" : ""}
        </span>
      : <span className="text-slate-300">—</span> },
  { key: "broken_reason", label: "Broken reason",
    text: (r) => r.broken_reason || "",
    render: (r) => r.broken_reason
      ? <span className="truncate text-xs text-rose-600 max-w-[220px] block" title={r.broken_reason}>{r.broken_reason}</span>
      : <span className="text-slate-300">—</span> },
  { key: "serial_key",    label: "Serial Key",
    text: (r) => r.serial_key || "",
    render: (r) => <span className="font-mono text-[10.5px] text-slate-600">{r.serial_key}</span> },
  { key: "owner",         label: "Owner",
    text: (r) => r.owner || "",
    render: (r) => <span className="text-slate-800">{r.owner || "—"}</span> },
  { key: "ticket",        label: "Ticket",
    text: (r) => r.ticket || "",
    render: (r) => <span className="font-mono text-xs text-slate-500">{r.ticket || "—"}</span> },
  { key: "remarks",       label: "Remarks",
    text: (r) => r.remarks || "",
    render: (r) => <span className="truncate text-xs text-slate-500 max-w-[220px] block" title={r.remarks}>{r.remarks || "—"}</span> },
  { key: "approver_name", label: "Approver",
    text: (r) => r.approver_name || "",
    render: (r) => r.approver_name
      ? <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-medium text-indigo-700"><ShieldCheck className="h-3 w-3" />{r.approver_name}</span>
      : <span className="text-slate-300">—</span> },
  { key: "approved_at",   label: "Approved at",
    text: (r) => fmtDT(r.approved_at),
    render: (r) => <span className="text-[11px] text-slate-500 tabular-nums">{fmtDT(r.approved_at) || "—"}</span> },
  { key: "date_created",  label: "Created",
    text: (r) => fmtDT(r.date_created),
    render: (r) => <span className="text-[11px] text-slate-500 tabular-nums">{fmtDT(r.date_created) || "—"}</span> },
  { key: "last_update",   label: "Last update",
    text: (r) => fmtDT(r.last_update),
    render: (r) => <span className="text-[11px] text-slate-500 tabular-nums">{fmtDT(r.last_update) || "—"}</span> },
  { key: "user_created",  label: "User Created",
    text: (r) => r.user_created || "",
    render: (r) => <span className="text-xs text-slate-500">{r.user_created || "—"}</span> },
];
const COL_MAP  = Object.fromEntries(COLUMNS.map((c) => [c.key, c]));
const ALL_KEYS = COLUMNS.map((c) => c.key);
const DEFAULT_HIDDEN = ["date_created", "user_created"];

// Iter 91s — compact 30-day sparkline used inside the "Top broken reasons"
// pills.  Renders a small SVG polyline + trend arrow so recruiters see
// whether a specific reason is accelerating or cooling off at a glance.
// No dependency on any chart library — keeps the pill light.
function Sparkline({ data = [], width = 60, height = 16 }) {
  if (!data.length) return null;
  const max = Math.max(...data, 1);
  const stepX = data.length > 1 ? width / (data.length - 1) : width;
  const points = data.map((v, i) => {
    const x = i * stepX;
    const y = height - (v / max) * (height - 2) - 1;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  // Trend arrow — compare last 7-day sum vs prior 7-day sum.
  const tail = data.slice(-7).reduce((a, b) => a + b, 0);
  const prev = data.slice(-14, -7).reduce((a, b) => a + b, 0);
  const trendUp   = tail > prev;
  const trendDown = tail < prev;
  const trend = trendUp ? "▲" : trendDown ? "▼" : "•";
  const trendColor = trendUp ? "#dc2626" : trendDown ? "#059669" : "#94a3b8";
  return (
    <span className="inline-flex items-center gap-1" aria-hidden>
      <svg width={width} height={height} className="overflow-visible">
        <polyline fill="none" stroke="#dc2626" strokeWidth="1.4"
                  strokeLinecap="round" strokeLinejoin="round"
                  points={points} />
      </svg>
      <span className="text-[10px] font-bold leading-none" style={{ color: trendColor }}>
        {trend}
      </span>
    </span>
  );
}

// Iter 91u — canonical seed reasons ops has agreed on.  New free-text
// values still work, but the datalist funnels typing towards these
// two so the heatmap stays consistent (and case-collapsed dedupe on
// the backend still handles minor casing drift).
const CANONICAL_BROKEN_REASONS = ["Replacement", "System error"];

const EMPTY = () => ({
  serial_key: "", owner: "", ticket: "", remarks: "",
  is_available: true, broken: false, approver_user_id: "",
});

export default function NetworkAssessmentKeys() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightId = searchParams.get("highlight") || "";
  const PREF_KEY = `network_keys_cols_v1_${user?.user_id || "anon"}`;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = usePersistedState("na-keys.q", "");
  const [availFilter, setAvailFilter] = usePersistedState("na-keys.avail", "all");
  const [brokenFilter, setBrokenFilter] = usePersistedState("na-keys.broken", "all");
  const [editing, setEditing] = useState(null);
  const [approvers, setApprovers] = useState({ items: [], current_user_id: null });
  const [sort, setSort] = usePersistedState("na-keys.sort", { key: "key_id", dir: "desc" });
  const [importing, setImporting] = useState(false);
  const [historyRow, setHistoryRow] = useState(null);
  // Iter 91p — bulk select + broken-reason prompt
  const [selected, setSelected]   = useState(new Set());
  const [bulkOpen, setBulkOpen]   = useState(false);
  const [bulkOwner, setBulkOwner] = useState("");
  const [bulkTicket, setBulkTicket] = useState("");
  const [bulkBusy, setBulkBusy]   = useState(false);
  const [brokenPrompt, setBrokenPrompt] = useState(null); // { key, reason }
  // Iter 91q — top broken reasons this month
  const [heatmap, setHeatmap] = useState(null);

  // Column prefs — same localStorage pattern as Hardware.jsx.
  const [order, setOrder] = useState(ALL_KEYS);
  const [hidden, setHidden] = useState(DEFAULT_HIDDEN);
  useEffect(() => {
    try {
      const p = JSON.parse(localStorage.getItem(PREF_KEY) || "{}");
      const ord = Array.isArray(p.order) ? p.order.filter((k) => COL_MAP[k]) : [];
      setOrder([...ord, ...ALL_KEYS.filter((k) => !ord.includes(k))]);
      if (Array.isArray(p.hidden)) setHidden(p.hidden.filter((k) => COL_MAP[k]));
    } catch { /* keep defaults */ }
  }, [PREF_KEY]);
  const persist = (o, h) => localStorage.setItem(PREF_KEY, JSON.stringify({ order: o, hidden: h }));
  const toggleHidden = (key) => setHidden((h) => {
    const nh = h.includes(key) ? h.filter((k) => k !== key) : [...h, key];
    persist(order, nh); return nh;
  });
  const moveCol = (key, dir) => {
    setOrder((cur) => {
      const idx = cur.indexOf(key);
      const tgt = idx + dir;
      if (idx < 0 || tgt < 0 || tgt >= cur.length) return cur;
      const next = [...cur];
      [next[idx], next[tgt]] = [next[tgt], next[idx]];
      persist(next, hidden); return next;
    });
  };
  const resetCols = () => { setOrder(ALL_KEYS); setHidden(DEFAULT_HIDDEN); persist(ALL_KEYS, DEFAULT_HIDDEN); };
  const toolbarColumns = useMemo(
    () => ALL_KEYS.map((k) => ({ id: k, label: COL_MAP[k]?.label || k, always: COL_MAP[k]?.always })),
    []
  );
  const toolbarVisibleIds = useMemo(
    () => order.filter((k) => !hidden.includes(k)), [order, hidden]);
  const visibleCols = useMemo(
    () => order.filter((k) => !hidden.includes(k)).map((k) => COL_MAP[k]).filter(Boolean),
    [order, hidden]);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, { data: appr }, { data: heat }] = await Promise.all([
        api.get("/network-keys?limit=20000"),
        api.get("/network-keys/staff-approvers"),
        api.get("/network-keys/broken-heatmap"),
      ]);
      setRows(data.items || []);
      setApprovers(appr);
      setHeatmap(heat);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load keys");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Iter 91o — deep-link support from Notification Centre.  A
  // `?highlight=<key_id>` param scrolls the row into view and flashes an
  // amber ring for a couple of seconds so the reviewer instantly spots
  // the key they were pinged about.
  useEffect(() => {
    if (!highlightId || loading || rows.length === 0) return;
    const t = setTimeout(() => {
      const el = document.querySelector(
        `[data-testid="network-keys-row-${highlightId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("ring-2", "ring-amber-400", "ring-offset-2",
                          "transition-shadow");
        setTimeout(() => {
          el.classList.remove("ring-2", "ring-amber-400", "ring-offset-2",
                              "transition-shadow");
          // Clear the query param so a refresh doesn't re-flash forever.
          const next = new URLSearchParams(searchParams);
          next.delete("highlight");
          setSearchParams(next, { replace: true });
        }, 2400);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [highlightId, loading, rows.length, searchParams, setSearchParams]);

  const filtered = useMemo(() => {  let out = rows;
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      out = out.filter((r) => [r.key_id, r.serial_key, r.owner, r.ticket, r.remarks, r.approver_name]
        .some((v) => (v || "").toLowerCase().includes(s)));
    }
    if (availFilter  !== "all") out = out.filter((r) => (availFilter  === "yes") === !!r.is_available);
    if (brokenFilter !== "all") out = out.filter((r) => (brokenFilter === "yes") === !!r.broken);
    return out;
  }, [rows, q, availFilter, brokenFilter]);

  const sorted = useMemo(() => {
    const col = COL_MAP[sort.key];
    if (!col) return filtered;
    const arr = [...filtered];
    arr.sort((a, b) => {
      const av = (col.text(a) || "").toLowerCase();
      const bv = (col.text(b) || "").toLowerCase();
      if (av < bv) return sort.dir === "asc" ? -1 : 1;
      if (av > bv) return sort.dir === "asc" ?  1 : -1;
      return 0;
    });
    return arr;
  }, [filtered, sort]);

  const pg = useTablePagination(sorted, 50);

  // Iter 91u — broken-reason autocomplete list.  Merges the two
  // canonical reasons ops agreed on ("Replacement", "System error")
  // with any distinct free-text reasons already on file, ordered by
  // frequency so the most-common values sit at the top of the
  // browser's native <datalist> popup.
  const reasonSuggestions = useMemo(() => {
    const counts = new Map();
    for (const r of rows) {
      const raw = (r.broken_reason || "").trim();
      if (!raw) continue;
      const key = raw.toLowerCase();
      counts.set(key, (counts.get(key) || 0) + 1);
    }
    const seen = new Set();
    const out  = [];
    // Canonical seeds first (locked casing so the heatmap dedupe is
    // deterministic even before real usage appears).
    for (const c of CANONICAL_BROKEN_REASONS) {
      out.push(c); seen.add(c.toLowerCase());
    }
    // Then existing values, sorted by frequency desc.
    const rowsMap = new Map();
    for (const r of rows) {
      const raw = (r.broken_reason || "").trim();
      if (!raw) continue;
      const key = raw.toLowerCase();
      if (seen.has(key)) continue;
      if (!rowsMap.has(key)) rowsMap.set(key, raw);
    }
    const extras = Array.from(rowsMap.entries())
      .sort((a, b) => (counts.get(b[0]) || 0) - (counts.get(a[0]) || 0))
      .map(([, display]) => display);
    return [...out, ...extras];
  }, [rows]);
  const toggleSort = (key) => setSort((s) => s.key === key
    ? { key, dir: s.dir === "asc" ? "desc" : "asc" }
    : { key, dir: "asc" });

  const save = async () => {
    try {
      const body = {
        serial_key:  editing.serial_key || "",
        owner:       editing.owner       || "",
        ticket:      editing.ticket      || "",
        remarks:     editing.remarks     || "",
        is_available: !!editing.is_available,
        broken:       !!editing.broken,
        approver_user_id: editing.approver_user_id || null,
      };
      if (editing.key_id) {
        await api.patch(`/network-keys/${editing.key_id}`, body);
        toast.success("Key updated");
      } else {
        await api.post("/network-keys", body);
        toast.success("Key created");
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    }
  };

  const remove = async (key) => {
    if (!window.confirm(`Delete key ${key.key_id}? This cannot be undone.`)) return;
    try {
      await api.delete(`/network-keys/${key.key_id}`);
      toast.success("Key deleted");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  // Inline approve / unapprove — enforces the audit rule client-side too.
  const toggleApprove = async (r) => {
    try {
      const nextApprover = r.approver_user_id ? "" : (approvers.current_user_id || "");
      await api.patch(`/network-keys/${r.key_id}`, { approver_user_id: nextApprover });
      toast.success(nextApprover ? `Approved as ${user?.name}` : "Approval cleared");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Approve failed");
    }
  };

  // Broken toggle — asks for a reason when flipping ON so we can spot
  // patterns across partners (iter 91p).  Healthy→broken flip goes via
  // the prompt dialog; broken→healthy flips inline and clears the reason.
  const toggleBroken = async (r) => {
    if (r.broken) {
      try {
        await api.patch(`/network-keys/${r.key_id}`, { broken: false });
        await load();
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
      }
      return;
    }
    setBrokenPrompt({ key: r, reason: "" });
  };
  const submitBrokenReason = async () => {
    if (!brokenPrompt?.key) return;
    try {
      await api.patch(`/network-keys/${brokenPrompt.key.key_id}`, {
        broken: true, broken_reason: (brokenPrompt.reason || "").trim(),
      });
      toast.success("Marked broken");
      setBrokenPrompt(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    }
  };

  // Bulk selection helpers.
  const toggleRow = (kid) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(kid)) n.delete(kid); else n.add(kid);
    return n;
  });
  const clearSelection = () => setSelected(new Set());
  const submitBulkAssign = async () => {
    if (selected.size === 0) return;
    if (!bulkOwner.trim() && !bulkTicket.trim()) {
      toast.error("Enter an owner or ticket to apply.");
      return;
    }
    try {
      setBulkBusy(true);
      const payload = { key_ids: Array.from(selected) };
      if (bulkOwner.trim())  payload.owner  = bulkOwner.trim();
      if (bulkTicket.trim()) payload.ticket = bulkTicket.trim();
      const { data } = await api.post("/network-keys/bulk-assign", payload);
      toast.success(`Updated ${data.updated} of ${data.requested} keys`);
      setBulkOpen(false); setBulkOwner(""); setBulkTicket(""); clearSelection();
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bulk assign failed");
    } finally { setBulkBusy(false); }
  };

  const exportCsv = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/network-keys/export.csv`;
    window.open(url, "_blank");
  };
  const exportPdf = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/network-keys/export.pdf`;
    window.open(url, "_blank");
  };

  const importCsv = async (file) => {
    if (!file) return;
    try {
      setImporting(true);
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/network-keys/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported: ${data.inserted} new · ${data.updated} updated · ${data.skipped} skipped`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Import failed");
    } finally { setImporting(false); }
  };

  return (
    <div className="space-y-4" data-testid="network-keys-page">
      <PageHeader icon={<KeyRound className="h-5 w-5 text-primary" />}
                  title="Network Assessment Keys"
                  subtitle="UUID licence keys handed out to partners for on-site network assessments." />

      {/* Iter 91q — Top broken reasons this month strip (+ iter 91s sparkline) */}
      {heatmap && heatmap.top_reasons?.length > 0 && (
        <Card className="p-3 border-rose-100 bg-rose-50/40"
              data-testid="network-keys-heatmap">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5 pr-2 text-xs font-semibold text-rose-700">
              <Flame className="h-3.5 w-3.5" />
              Top broken reasons — {heatmap.month}
              <span className="ml-1 rounded-full bg-rose-100 px-1.5 py-0.5 text-[10px] font-bold text-rose-700">
                {heatmap.total_this_month}
              </span>
            </div>
            {heatmap.top_reasons.map((r, i) => (
              <button key={i}
                      onClick={() => setQ(r.reason)}
                      title={`${r.count} broken · ${r.owners.join(", ") || "no owners"} · last 30 days: ${(r.spark || []).join(",")}`}
                      data-testid={`network-keys-heatmap-pill-${i}`}
                      className="group inline-flex items-center gap-1.5 rounded-full border border-rose-200 bg-white px-2.5 py-1 text-xs text-rose-800 shadow-sm hover:border-rose-400 hover:bg-rose-100 transition-colors">
                <span className="max-w-[180px] truncate font-medium">{r.reason}</span>
                <span className="rounded-full bg-rose-600 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white">
                  {r.count}
                </span>
                <Sparkline data={r.spark || []} />
                {r.owners.length > 0 && (
                  <span className="hidden text-[10px] text-rose-500 sm:inline">
                    · {r.owners.slice(0, 2).join(", ")}{r.owners.length > 2 ? "…" : ""}
                  </span>
                )}
              </button>
            ))}
          </div>
        </Card>
      )}

      <Card className="p-4 space-y-3">
        <div className="flex flex-wrap items-end gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Search keys, owner, ticket…"
                   className="pl-8 w-64"
                   data-testid="network-keys-search" />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase text-slate-400">Available</Label>
            <Select value={availFilter} onValueChange={setAvailFilter}>
              <SelectTrigger className="w-32 h-9" data-testid="network-keys-avail-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="yes">Available</SelectItem>
                <SelectItem value="no">In use</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase text-slate-400">Broken</Label>
            <Select value={brokenFilter} onValueChange={setBrokenFilter}>
              <SelectTrigger className="w-32 h-9" data-testid="network-keys-broken-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="yes">Broken</SelectItem>
                <SelectItem value="no">Healthy</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <ColumnsToolbar
            count={sorted.length} label="key" loading={loading}
            columns={toolbarColumns}
            visibleIds={toolbarVisibleIds}
            onToggle={toggleHidden}
            onMove={moveCol}
            onReset={resetCols}
            onExportCsv={exportCsv}
            onExportPdf={exportPdf}
            testidPrefix="network-keys"
            extraLeft={(
              <>
                <label className="inline-flex items-center gap-1.5">
                  <input type="file" accept=".csv" className="hidden"
                         data-testid="network-keys-import-file"
                         onChange={(e) => { importCsv(e.target.files?.[0]); e.target.value = ""; }} />
                  <Button variant="outline" size="sm" asChild
                          disabled={importing}
                          data-testid="network-keys-import-btn" className="h-9">
                    <span className="inline-flex items-center gap-1.5 cursor-pointer">
                      {importing
                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        : <Upload className="h-3.5 w-3.5" />}
                      Import CSV
                    </span>
                  </Button>
                </label>
                <Button size="sm" onClick={() => setEditing(EMPTY())}
                        data-testid="network-keys-new-btn" className="h-9 gap-1.5">
                  <Plus className="h-3.5 w-3.5" /> New key
                </Button>
              </>
            )}
          />
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-left text-[10px] uppercase tracking-wider text-slate-500">
                <th className="w-8 px-2 py-2">
                  <button
                    onClick={() => {
                      if (selected.size === pg.pageItems.length && pg.pageItems.length > 0) {
                        clearSelection();
                      } else {
                        setSelected(new Set(pg.pageItems.map((r) => r.key_id)));
                      }
                    }}
                    className="text-slate-400 hover:text-slate-700"
                    data-testid="network-keys-select-all"
                    title="Select all on this page">
                    {selected.size === pg.pageItems.length && pg.pageItems.length > 0
                      ? <CheckSquare className="h-4 w-4" />
                      : <Square className="h-4 w-4" />}
                  </button>
                </th>
                {visibleCols.map((c) => (
                  <th key={c.key}
                      onClick={() => toggleSort(c.key)}
                      className="cursor-pointer select-none px-3 py-2 hover:bg-slate-50"
                      data-testid={`network-keys-th-${c.key}`}>
                    <span className="inline-flex items-center gap-1">
                      {c.label}
                      {sort.key === c.key
                        ? (sort.dir === "asc"
                            ? <ArrowUp className="h-3 w-3" />
                            : <ArrowDown className="h-3 w-3" />)
                        : <ArrowUpDown className="h-3 w-3 text-slate-300" />}
                    </span>
                  </th>
                ))}
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={visibleCols.length + 2} className="py-6 text-center">
                  <Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" />
                </td></tr>
              )}
              {!loading && pg.pageItems.length === 0 && (
                <tr><td colSpan={visibleCols.length + 2} className="py-6 text-center text-sm text-slate-400">
                  No keys match your filters.
                </td></tr>
              )}
              {!loading && pg.pageItems.map((r) => (
                <tr key={r.key_id}
                    className={`border-b border-slate-50 hover:bg-slate-50/60 ${selected.has(r.key_id) ? "bg-primary/5" : ""}`}
                    data-testid={`network-keys-row-${r.key_id}`}>
                  <td className="px-2 py-2 align-top">
                    <Checkbox checked={selected.has(r.key_id)}
                              onCheckedChange={() => toggleRow(r.key_id)}
                              data-testid={`network-keys-select-${r.key_id}`} />
                  </td>
                  {visibleCols.map((c) => (
                    <td key={c.key} className="px-3 py-2 align-top">{c.render(r)}</td>
                  ))}
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button title={r.approver_name ? "Clear approval" : "Approve as me"}
                            onClick={() => toggleApprove(r)}
                            data-testid={`network-keys-approve-${r.key_id}`}
                            className={`mr-1 rounded p-1 ${r.approver_name ? "text-indigo-600 hover:bg-indigo-50" : "text-slate-300 hover:text-indigo-600 hover:bg-slate-100"}`}>
                      <ShieldCheck className="h-4 w-4" />
                    </button>
                    <button title={r.broken ? "Mark healthy" : "Mark broken"}
                            onClick={() => toggleBroken(r)}
                            data-testid={`network-keys-broken-${r.key_id}`}
                            className={`mr-1 rounded p-1 ${r.broken ? "text-rose-600 hover:bg-rose-50" : "text-slate-300 hover:text-rose-600 hover:bg-slate-100"}`}>
                      <Wrench className="h-4 w-4" />
                    </button>
                    <button title="History"
                            onClick={() => setHistoryRow(r)}
                            data-testid={`network-keys-history-${r.key_id}`}
                            className="mr-1 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
                      <History className="h-4 w-4" />
                    </button>
                    <button title="Edit"
                            onClick={() => setEditing({ ...r })}
                            data-testid={`network-keys-edit-${r.key_id}`}
                            className="mr-1 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-primary">
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button title="Delete"
                            onClick={() => remove(r)}
                            data-testid={`network-keys-delete-${r.key_id}`}
                            className="rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between">
          <PageSizeSelect pageSize={pg.pageSize} setPageSize={pg.setPageSize} />
          <TablePagination {...pg} testid="network-keys-pagination" />
        </div>
      </Card>

      {/* Iter 91p — floating bulk-assign action bar */}
      {selected.size > 0 && (
        <div className="fixed bottom-4 left-1/2 z-40 flex -translate-x-1/2 items-center gap-3 rounded-full border border-indigo-200 bg-white px-4 py-2 shadow-lg"
             data-testid="network-keys-bulk-bar">
          <span className="text-sm font-medium text-indigo-700">
            {selected.size} key{selected.size === 1 ? "" : "s"} selected
          </span>
          <Button size="sm" onClick={() => setBulkOpen(true)}
                  data-testid="network-keys-bulk-open" className="gap-1.5">
            <Users className="h-3.5 w-3.5" /> Bulk assign
          </Button>
          <Button size="sm" variant="outline" onClick={clearSelection}
                  data-testid="network-keys-bulk-clear" className="gap-1.5">
            <X className="h-3.5 w-3.5" /> Clear
          </Button>
        </div>
      )}

      {/* Bulk assign dialog */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-md" data-testid="network-keys-bulk-dialog">
          <DialogHeader>
            <DialogTitle>Bulk assign {selected.size} keys</DialogTitle>
            <DialogDescription>
              Set the Owner and/or Ticket on every selected key.  Leave a
              field empty to skip it (existing values stay).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Owner</Label>
              <Input value={bulkOwner} onChange={(e) => setBulkOwner(e.target.value)}
                     placeholder="Owner name / company"
                     data-testid="network-keys-bulk-owner" />
            </div>
            <div>
              <Label>Ticket #</Label>
              <Input value={bulkTicket} onChange={(e) => setBulkTicket(e.target.value)}
                     placeholder="e.g. TCK-1234"
                     data-testid="network-keys-bulk-ticket" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)} disabled={bulkBusy}>
              Cancel
            </Button>
            <Button onClick={submitBulkAssign} disabled={bulkBusy}
                    data-testid="network-keys-bulk-apply" className="gap-1.5">
              {bulkBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ticket className="h-4 w-4" />}
              Apply to {selected.size} keys
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Broken-reason prompt (iter 91p) */}
      <Dialog open={!!brokenPrompt} onOpenChange={(v) => !v && setBrokenPrompt(null)}>
        <DialogContent className="max-w-md" data-testid="network-keys-broken-prompt">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <Wrench className="h-4 w-4" />
              Mark {brokenPrompt?.key?.key_id} broken
            </DialogTitle>
            <DialogDescription>
              Add a short reason so we can spot patterns across partners
              (e.g. &ldquo;fails activation&rdquo;, &ldquo;bad batch — Q3 2026&rdquo;).
              Optional but strongly encouraged.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label>Reason</Label>
            <Input autoFocus
                   list="network-keys-broken-reason-suggestions"
                   value={brokenPrompt?.reason || ""}
                   onChange={(e) => setBrokenPrompt((p) => ({ ...p, reason: e.target.value }))}
                   placeholder="Start typing… (Replacement, System error, …)"
                   data-testid="network-keys-broken-reason-input" />
            <datalist id="network-keys-broken-reason-suggestions"
                      data-testid="network-keys-broken-reason-datalist">
              {reasonSuggestions.map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
            <div className="mt-1 text-[10px] text-slate-400">
              Suggestions come from previously-used values plus two
              canonical seeds — reuse where possible so the heatmap
              stays clean.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBrokenPrompt(null)}>Cancel</Button>
            <Button onClick={submitBrokenReason}
                    className="bg-rose-600 hover:bg-rose-700 text-white gap-1.5"
                    data-testid="network-keys-broken-reason-save">
              <Wrench className="h-4 w-4" /> Mark broken
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-lg" data-testid="network-keys-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.key_id ? `Edit ${editing.key_id}` : "New key"}</DialogTitle>
            <DialogDescription>
              Serial keys are UUIDs handed to partners.  Approver must be the
              signed-in staff — this is enforced server-side as an audit rule.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Serial key (UUID)</Label>
                  <Input value={editing.serial_key || ""}
                         onChange={(e) => setEditing({ ...editing, serial_key: e.target.value })}
                         placeholder="Leave blank to auto-generate"
                         data-testid="network-keys-dialog-serial"
                         className="font-mono text-xs" />
                </div>
                <div>
                  <Label>Ticket #</Label>
                  <Input value={editing.ticket || ""}
                         onChange={(e) => setEditing({ ...editing, ticket: e.target.value })}
                         data-testid="network-keys-dialog-ticket" />
                </div>
              </div>
              <div>
                <Label>Owner</Label>
                <Input value={editing.owner || ""}
                       onChange={(e) => setEditing({ ...editing, owner: e.target.value })}
                       data-testid="network-keys-dialog-owner" />
              </div>
              <div>
                <Label>Remarks</Label>
                <Textarea rows={3} value={editing.remarks || ""}
                          onChange={(e) => setEditing({ ...editing, remarks: e.target.value })}
                          data-testid="network-keys-dialog-remarks" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
                  <div>
                    <div className="text-sm font-medium text-slate-700">Available</div>
                    <div className="text-[10px] text-slate-400">Uncheck when a key is in-use.</div>
                  </div>
                  <Switch checked={!!editing.is_available}
                          onCheckedChange={(v) => setEditing({ ...editing, is_available: v })}
                          data-testid="network-keys-dialog-avail" />
                </div>
                <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
                  <div>
                    <div className="text-sm font-medium text-slate-700">Broken</div>
                    <div className="text-[10px] text-slate-400">Flag if the key won&apos;t validate.</div>
                  </div>
                  <Switch checked={!!editing.broken}
                          onCheckedChange={(v) => setEditing({ ...editing, broken: v })}
                          data-testid="network-keys-dialog-broken" />
                </div>
              </div>
              <div>
                <Label>Approver</Label>
                <Select value={editing.approver_user_id || "__none__"}
                        onValueChange={(v) => setEditing({ ...editing, approver_user_id: v === "__none__" ? "" : v })}>
                  <SelectTrigger data-testid="network-keys-dialog-approver">
                    <SelectValue placeholder="Select approver" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— None —</SelectItem>
                    {(approvers.items || []).map((s) => {
                      const isSelf = s.user_id === approvers.current_user_id;
                      return (
                        <SelectItem key={s.user_id} value={s.user_id}
                                    disabled={!isSelf}
                                    data-testid={`network-keys-approver-opt-${s.user_id}`}>
                          {s.name} {isSelf ? "(you)" : "· locked"}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                <div className="mt-1 text-[10px] text-slate-400">
                  Audit rule — you can only mark yourself as the approver.
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>
              <X className="h-4 w-4" /> Cancel
            </Button>
            <Button onClick={save} data-testid="network-keys-dialog-save">
              <Save className="h-4 w-4" /> Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History dialog */}
      <Dialog open={!!historyRow} onOpenChange={(v) => !v && setHistoryRow(null)}>
        <DialogContent className="max-w-lg" data-testid="network-keys-history-dialog">
          <DialogHeader>
            <DialogTitle>History — {historyRow?.key_id}</DialogTitle>
            <DialogDescription>Every touch on this key is recorded.</DialogDescription>
          </DialogHeader>
          <div className="max-h-80 overflow-y-auto space-y-1.5">
            {(historyRow?.history || []).slice().reverse().map((h, i) => (
              <div key={i} className="rounded border border-slate-100 px-2 py-1.5 text-xs">
                <div className="text-[10px] uppercase text-slate-400">
                  {fmtDT(h.at)} · {h.actor_name || h.actor_user_id || "system"}
                </div>
                <div className="text-slate-700">
                  {h.action}
                  {h.field ? ` · ${h.field}` : ""}
                  {h.from !== undefined && h.to !== undefined
                    ? <> · <span className="text-slate-400">{String(h.from ?? "—")}</span> → <span className="font-medium">{String(h.to ?? "—")}</span></>
                    : null}
                </div>
              </div>
            ))}
            {(historyRow?.history || []).length === 0 && (
              <div className="py-4 text-center text-xs text-slate-400">No history yet.</div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
